import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import {
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut,
  User,
} from 'firebase/auth';
import { auth } from '../../firebase.ts';
import '../EmployeeSurvey.css';

const isCompanyDomain = (email: string | null | undefined): boolean =>
  !!email && email.toLowerCase().endsWith('@sweetspotfarms.com');

interface SurveyAuthGateProps {
  children: (signOutHandler: () => void, user: User) => React.ReactNode;
  /**
   * If provided, additionally restricts access to these specific email
   * addresses (case-insensitive). When omitted, any @sweetspotfarms.com
   * Google account is allowed.
   */
  allowedEmails?: string[];
  /** Page <title> while the gate is showing. */
  pageTitle?: string;
  /** Small uppercase tag above the headline. */
  signInBadge?: string;
  /** Main headline on the sign-in card. */
  signInTitle?: string;
  /** Body copy under the headline. */
  signInDescription?: React.ReactNode;
  /** Optional anonymity / context note in the highlight box. */
  anonNote?: React.ReactNode;
  /** Copy when the signed-in user is on @sweetspotfarms.com but not in the allowlist. */
  notAllowedMessage?: string;
}

/**
 * Firebase Google sign-in gate used by the public-but-employees-only
 * survey URL and the results viewer.
 *
 * Two-tier access: (1) verifies the signed-in account is on the
 * @sweetspotfarms.com domain; (2) if `allowedEmails` is provided, also
 * checks the email is in that list.
 *
 * The signed-in user is passed back to children so the results viewer
 * can render UI conditioned on identity. The survey form ignores the
 * `user` argument because submissions are anonymous.
 */
const SurveyAuthGate: React.FC<SurveyAuthGateProps> = ({
  children,
  allowedEmails,
  pageTitle = 'Sweetspot — Employee Engagement Survey',
  signInBadge = 'Employees Only',
  signInTitle = 'Sign in to continue',
  signInDescription = (
    <>
      This survey is for Sweetspot employees only. Sign in with your <strong>@sweetspotfarms.com</strong> Google
      account to verify you're on the team. Your responses stay anonymous — we don't store your identity
      alongside your answers.
    </>
  ),
  anonNote = (
    <>
      Signing in confirms you're an employee, but the submission you send afterwards does not include your name,
      email, or any other identifier.
    </>
  ),
  notAllowedMessage = 'This account is not authorized for this page.',
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [authChecked, setAuthChecked] = useState<boolean>(false);
  const [signingIn, setSigningIn] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const allowlistLower = (allowedEmails ?? []).map(e => e.trim().toLowerCase());

  const isAllowed = (email: string | null | undefined): boolean => {
    if (!isCompanyDomain(email)) return false;
    if (allowlistLower.length === 0) return true;
    return allowlistLower.includes((email as string).toLowerCase());
  };

  useEffect(() => {
    getRedirectResult(auth)
      .then(result => {
        if (result && !isCompanyDomain(result.user.email)) {
          return auth.signOut().then(() => {
            setError('Access restricted to @sweetspotfarms.com email addresses.');
          });
        }
      })
      .catch(() => {
        setError('Failed to complete Google sign-in. Please try again.');
      });

    const unsubscribe = onAuthStateChanged(auth, current => {
      setUser(current);
      setAuthChecked(true);
    });
    return () => unsubscribe();
  }, []);

  const handleSignIn = async () => {
    setError(null);
    setSigningIn(true);
    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ hd: 'sweetspotfarms.com' });

      let result;
      try {
        result = await signInWithPopup(auth, provider);
      } catch (popupError: any) {
        if (
          popupError?.code === 'auth/popup-blocked' ||
          popupError?.code === 'auth/cancelled-popup-request' ||
          popupError?.message?.includes('Cross-Origin-Opener-Policy')
        ) {
          await signInWithRedirect(auth, provider);
          return;
        }
        throw popupError;
      }

      if (!isCompanyDomain(result.user.email)) {
        await auth.signOut();
        setError('Access restricted to @sweetspotfarms.com email addresses.');
      }
    } catch (err: any) {
      if (err?.code === 'auth/popup-closed-by-user') {
        setError('Sign-in was cancelled.');
      } else {
        setError('Failed to sign in with Google. Please try again.');
      }
    } finally {
      setSigningIn(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
    } catch {
      /* ignore */
    }
  };

  if (!authChecked) {
    return (
      <div className="employee-survey">
        <div className="lbg"><div className="sh s1" /><div className="sh s2" /><div className="sh s3" /></div>
        <div className="ww">
          <div className="wel" style={{ color: 'var(--tm)', fontSize: 14 }}>Checking sign-in…</div>
        </div>
      </div>
    );
  }

  const userIsSignedInButNotAllowed =
    !!user && isCompanyDomain(user.email) && allowlistLower.length > 0 && !isAllowed(user.email);

  if (!user || !isAllowed(user.email)) {
    return (
      <div className="employee-survey">
        <Helmet>
          <title>{pageTitle}</title>
          <meta name="robots" content="noindex, nofollow" />
          <link
            href="https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,600;0,9..144,700;1,9..144,400&family=Work+Sans:wght@300;400;500;600;700&display=swap"
            rel="stylesheet"
          />
        </Helmet>
        <div className="lbg"><div className="sh s1" /><div className="sh s2" /><div className="sh s3" /></div>
        <div className="ww">
          <div className="wel">
            <div className="hb">{signInBadge}</div>
            <h1>{signInTitle}</h1>
            <p className="sub">{signInDescription}</p>
            {anonNote && (
              <div className="anon-note" style={{ textAlign: 'left' }}>
                <span className="lock">🔒</span>
                <span>{anonNote}</span>
              </div>
            )}
            {!userIsSignedInButNotAllowed && (
              <button
                type="button"
                className="bbeg"
                onClick={handleSignIn}
                disabled={signingIn}
              >
                {signingIn ? 'Signing in…' : 'Sign in with Google'}
              </button>
            )}
            {userIsSignedInButNotAllowed && (
              <>
                <div className="submit-status error" style={{ marginTop: 14 }}>
                  {notAllowedMessage}
                </div>
                <button
                  type="button"
                  className="bprev"
                  style={{ marginTop: 14 }}
                  onClick={handleSignOut}
                >
                  Sign out
                </button>
              </>
            )}
            {error && (
              <div className="submit-status error" style={{ marginTop: 14 }}>
                {error}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return <>{children(handleSignOut, user)}</>;
};

export default SurveyAuthGate;
